import { EntriesList } from "@dash/history";
import { Skeleton } from "@dash/skeleton";
import {
  docAppLabel,
  docHref,
  docTypeIcon,
  docTypeLabel,
  getDocDisplayName,
  HistoryBrowserEntry,
  useRecentlyViewedDocs,
} from "@megarax/features";
import { MdHistory } from "react-icons/md";
import { useNavigate } from "react-router-dom-v5-compat";

import { getMegaraxPath } from "@megaron/megarax-uri";

import { SectionCard, SectionCardProps } from "./SectionCard";

const recentLimit = 5;
const skeletonRows = [0, 1, 2, 3, 4];

const cardProps: Omit<SectionCardProps, "children"> = {
  header: "Ostatnie dokumenty",
  linkTo: getMegaraxPath.uri("megarax://history"),
  linkLabel: "Cała historia",
  linkIcon: <MdHistory />,
};

const formatTime = (date: Date): string => date.toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" });

export const RecentDocsCard: React.FC = () => {
  const navigate = useNavigate();
  const { data: docs } = useRecentlyViewedDocs(recentLimit);

  if (docs === undefined)
    return (
      <SectionCard {...cardProps}>
        <RecentDocsSkeleton />
      </SectionCard>
    );

  if (docs.length === 0) return null;

  return (
    <SectionCard {...cardProps}>
      <EntriesList>
        {docs.map(({ doc, viewedAt }) => {
          const href = docHref(doc.docType, doc.docId);
          const Icon = docTypeIcon(doc.docType);

          if (!href) return null;

          return (
            <HistoryBrowserEntry
              key={`${doc.docId}-${viewedAt.getTime()}`}
              title={getDocDisplayName(doc)}
              href={href}
              icon={<Icon />}
              time={formatTime(viewedAt)}
              appLabel={docAppLabel(doc.docType)}
              docTypeLabel={docTypeLabel(doc.docType)}
              onClick={() => navigate(href)}
            />
          );
        })}
      </EntriesList>
    </SectionCard>
  );
};

const RecentDocsSkeleton: React.FC = () => (
  <div css={{ display: "flex", flexDirection: "column", gap: "1rem", width: "100%" }}>
    {skeletonRows.map((row) => (
      <div key={row} css={{ display: "flex", gap: "0.625rem", alignItems: "flex-start", minHeight: "40px" }}>
        <div css={{ width: "30px", height: "30px", flexShrink: 0, borderRadius: "50%", overflow: "hidden" }}>
          <Skeleton width="30px" height="30px" />
        </div>

        <div css={{ display: "flex", flexDirection: "column", gap: "0.3125rem", flex: 1, minWidth: 0 }}>
          <Skeleton width={`${70 - row * 6}%`} height="0.875rem" />
          <Skeleton width={`${55 - row * 5}%`} height="0.75rem" />
        </div>
      </div>
    ))}
  </div>
);
